import React, { useState, useMemo } from "react";
import { Lightbox, LightboxTrigger, LightboxContent } from "@/components/UI/lightbox";
import { FilterOptionsPanel } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/DocHierarchyFilter/comps/FilterOptionsPanel";
import { AnnoHierarchyFilter } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/AnnoHierarchyFilter";
import { TopicClusterFilter } from "@/components/inputs/DynamicTagsInputs/FilterInput/comps/TopicClusterFilter";


/**
 * Unified lightbox for applied filter blocks (both doc and anno).
 *
 * - Without annoFilterConfig: renders FilterOptionsPanel (doc filters, common anno filters)
 * - With annoFilterConfig: renders AnnoHierarchyFilter scoped to the block's level,
 *   or FilterOptionsPanel for commonAnnoFilter blocks
 */
export const FilterLightbox = ({ block, activeFilters, onChange, annoFilterConfig, onOpenChange, children, contentTypes }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const isCommonAnnoFilter = block.level === "commonAnnoFilter";
  const isAnnoHierarchy = !!annoFilterConfig && !isCommonAnnoFilter;
  // Split mode's Topics filter: a tree of clustering nodes, not fetched
  // options — it has its own picker (the same one the sidebar's filter uses).
  const isTopicsFilter = block.filterConfig?.source?.filterType === "topicClusteringNodeType";

  const currentValues = useMemo(
    () => activeFilters?.find((f) => f.filterId === block.filterId)?.values || [],
    [activeFilters, block.filterId]
  );

  const needsOverflow = block.filterConfig?.source?.filterType === "dateRangeType";

  // Anno hierarchy needs object format { filterId: values[] }
  const selectedValues = useMemo(() => {
    if (!isAnnoHierarchy) return null;
    const result = {};
    for (const f of activeFilters) result[f.filterId] = f.values || [];
    return result;
  }, [isAnnoHierarchy, activeFilters]);

  const initialSelectedTheme = useMemo(
    () => block.parentThemeId
      ? { _id: block.parentThemeId, display: block.breadcrumb[0] || "" }
      : null,
    [block.parentThemeId, block.breadcrumb]
  );

  const initialSelectedSubTheme = useMemo(
    () => block.parentSubThemeId
      ? {
          _id: block.parentSubThemeId,
          display: block.breadcrumb.length > 1
            ? block.breadcrumb[1]
            : block.breadcrumb[0] || "",
        }
      : null,
    [block.parentSubThemeId, block.breadcrumb]
  );

  const contentStyle = {
    overflow: needsOverflow ? "visible" : "hidden",
    zIndex: 99999,
  };

  return (
    <Lightbox multilayer={!needsOverflow} placement="right-start" open={isOpen} onOpenChange={handleOpenChange}>
      <LightboxTrigger asChild>
        {children}
      </LightboxTrigger>

      <LightboxContent cascade style={contentStyle}>
        {isAnnoHierarchy ? (
          <AnnoHierarchyFilter
            filterConfig={annoFilterConfig}
            value={selectedValues}
            onChange={onChange}
            scopeToLevel={block.level}
            initialSelectedTheme={initialSelectedTheme}
            initialSelectedSubTheme={initialSelectedSubTheme}
          />
        ) : isTopicsFilter ? (
          <TopicClusterFilter
            filterConfig={block.filterConfig}
            value={currentValues}
            onChange={onChange}
          />
        ) : block.filterConfig ? (
          <FilterOptionsPanel
            filterConfig={block.filterConfig}
            value={currentValues}
            onChange={(newValues) => onChange(block.filterId, newValues)}
            contentTypes={contentTypes}
          />
        ) : null}
      </LightboxContent>
    </Lightbox>
  );
};
